import { KeyValuePairSize } from '../KeyValuePairItem/constants';

import type { KeyValuePairsColumnSpan } from './types';

export interface ColumnSpanItem {
  columnSpan?: KeyValuePairsColumnSpan;
}

export interface PackedRowItem<T> {
  columnSpan: number;
  item: T;
  itemIndex: number;
}

export type PackedRow<T> = PackedRowItem<T>[];

const normalizeAvailableColumnCount = (availableColumnCount: number) => {
  if (!Number.isFinite(availableColumnCount) || availableColumnCount < 1) {
    return 1;
  }

  return Math.floor(availableColumnCount);
};

/**
 * 컨테이너 너비에 들어갈 수 있는 실제 컬럼 수를 계산합니다.
 * 컬럼 사이에는 KeyValuePairs의 기본 column gap(32px)을 반영합니다.
 *
 * @param containerWidth - 컨테이너의 현재 너비 (px)
 * @param minColumnWidth - 개별 컬럼이 보장해야 하는 최소 너비 (px)
 * @param maxColumnRepeat - 최대 컬럼 수. 0 또는 유효하지 않은 값은 제한 없음으로 처리합니다.
 */
export const getAvailableColumnCount = (
  containerWidth: number,
  minColumnWidth: number,
  maxColumnRepeat: number,
) => {
  const normalizedContainerWidth =
    Number.isFinite(containerWidth) && containerWidth > 0 ? containerWidth : 0;
  const normalizedMinColumnWidth =
    Number.isFinite(minColumnWidth) && minColumnWidth > 0 ? minColumnWidth : 0;
  const columnGap = KeyValuePairSize.COLUMN_GAP;
  const columnCount = Math.max(
    1,
    Math.floor((normalizedContainerWidth + columnGap) / (normalizedMinColumnWidth + columnGap)),
  );

  if (!Number.isFinite(maxColumnRepeat) || maxColumnRepeat <= 0) {
    return columnCount;
  }

  return Math.min(columnCount, Math.max(1, Math.floor(maxColumnRepeat)));
};

/**
 * columnSpan 입력을 현재 사용할 수 있는 컬럼 범위의 양의 정수로 정규화합니다.
 */
export const normalizeColumnSpan = (
  columnSpan: Exclude<KeyValuePairsColumnSpan, 'remaining'> | undefined,
  availableColumnCount: number,
) => {
  const normalizedAvailableColumnCount = normalizeAvailableColumnCount(availableColumnCount);

  if (columnSpan === 'all') {
    return normalizedAvailableColumnCount;
  }

  if (columnSpan === undefined || !Number.isFinite(columnSpan) || columnSpan <= 0) {
    return 1;
  }

  return Math.min(Math.max(1, Math.floor(columnSpan)), normalizedAvailableColumnCount);
};

/**
 * 원본 순서를 유지하며 아이템을 행 단위로 배치합니다.
 * 현재 행의 남은 칸보다 span이 크면 다음 행으로 넘기고, 남은 칸은 빈 상태로 둡니다.
 * `'remaining'`은 현재 행의 남은 칸을 모두 차지하며 해당 행을 종료합니다.
 */
export const packItemsIntoRows = <T extends ColumnSpanItem>(
  items: readonly T[],
  availableColumnCount: number,
) => {
  const normalizedAvailableColumnCount = normalizeAvailableColumnCount(availableColumnCount);
  const rows: PackedRow<T>[] = [];
  let currentRow: PackedRow<T> = [];
  let occupiedColumnCount = 0;

  items.forEach((item, itemIndex) => {
    const columnSpan =
      item.columnSpan === 'remaining'
        ? normalizedAvailableColumnCount - occupiedColumnCount
        : normalizeColumnSpan(item.columnSpan, normalizedAvailableColumnCount);

    if (
      currentRow.length > 0 &&
      occupiedColumnCount + columnSpan > normalizedAvailableColumnCount
    ) {
      rows.push(currentRow);
      currentRow = [];
      occupiedColumnCount = 0;
    }

    currentRow.push({ item, itemIndex, columnSpan });
    occupiedColumnCount += columnSpan;

    if (occupiedColumnCount === normalizedAvailableColumnCount) {
      rows.push(currentRow);
      currentRow = [];
      occupiedColumnCount = 0;
    }
  });

  if (currentRow.length > 0) {
    rows.push(currentRow);
  }

  return rows;
};
